import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSession } from '../../firebase/UserProvider';
import ReactFlow, {
    Background,
    Controls,
    Handle,
    MiniMap,
    Panel,
    Position,
    addEdge,
    useNodesState,
    useEdgesState,
    MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

// ── node colour palette ───────────────────────────────────────────────────────
const PALETTE = [
    { key: 'indigo', tint: '#ECEEF8', accent: '#3B3B6D', text: '#1A1D21', label: 'Indigo'  },
    { key: 'violet', tint: '#F3EDFB', accent: '#6D28D9', text: '#1A1D21', label: 'Violet'  },
    { key: 'blue',   tint: '#EBF3FC', accent: '#1E5FA8', text: '#1A1D21', label: 'Blue'    },
    { key: 'teal',   tint: '#E6F4F2', accent: '#0F766E', text: '#1A1D21', label: 'Teal'    },
    { key: 'green',  tint: '#EBF5EC', accent: '#2E7D32', text: '#1A1D21', label: 'Green'   },
    { key: 'amber',  tint: '#FEF6E7', accent: '#B45309', text: '#1A1D21', label: 'Amber'   },
    { key: 'rose',   tint: '#FDEEF0', accent: '#9F3340', text: '#1A1D21', label: 'Rose'    },
    { key: 'mauve',  tint: '#F5EFF5', accent: '#7A4F75', text: '#1A1D21', label: 'Mauve'   },
    { key: 'stone',  tint: '#F2EFED', accent: '#6B534A', text: '#1A1D21', label: 'Stone'   },
    { key: 'slate',  tint: '#EEF1F4', accent: '#445566', text: '#1A1D21', label: 'Slate'   },
];

const paletteByKey = Object.fromEntries(PALETTE.map((p) => [p.key, p]));

// ── roles ─────────────────────────────────────────────────────────────────────
// Admin is excluded — admins can execute every step by definition.
const ROLES = [
    { key: 'Policymaker',        color: '#4338CA', bg: '#EEF2FF', label: 'Policymaker'        },
    { key: 'Manager',            color: '#7C3AED', bg: '#F5F3FF', label: 'Manager'            },
    { key: 'Analyst',            color: '#1D4ED8', bg: '#EFF6FF', label: 'Analyst'            },
    { key: 'Collection Manager', color: '#B45309', bg: '#FFFBEB', label: 'Collection Manager' },
    { key: 'Collector',          color: '#0F766E', bg: '#F0FDFA', label: 'Collector'          },
    { key: 'Reviewer',           color: '#9F3340', bg: '#FFF1F2', label: 'Reviewer'           },
    { key: 'Briefer',            color: '#2E7D32', bg: '#F0FDF4', label: 'Briefer'            },
    { key: 'Disclosure Officer', color: '#0E7490', bg: '#ECFEFF', label: 'Disclosure Officer' },
];

const roleByKey = Object.fromEntries(ROLES.map((r) => [r.key, r]));

// ── edge style ────────────────────────────────────────────────────────────────
const EDGE_COLOR = '#94A3B8';
const EDGE_BASE  = {
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: EDGE_COLOR },
    style: { stroke: EDGE_COLOR, strokeWidth: 1.5 },
    labelStyle: { fontSize: 10, fontWeight: 600, fill: '#64748B', fontFamily: 'Inter, sans-serif' },
    labelBgStyle: { fill: '#F8FAFC', fillOpacity: 0.95 },
    labelBgPadding: [5, 2],
    labelBgBorderRadius: 4,
};

function mkEdge(id, source, target, opts = {}) {
    return {
        ...EDGE_BASE,
        id, source, target,
        label: opts.label,
        style: { ...EDGE_BASE.style, ...(opts.dashed ? { strokeDasharray: '5 4' } : {}) },
    };
}

// ── step metadata ─────────────────────────────────────────────────────────────
const STEP_META = {
    pitch:           { description: null,                                                                                                  roles: ['Policymaker'],                          link: null                               },
    icr:             { description: 'Intelligence Collection Requirement — formal tasking that defines what intelligence is needed and why', roles: ['Analyst'],                            link: '/icr?status=active',               roleLinks: { policymaker: '/ICR' }     },
    sitrep:          { description: 'Situation Report — current operational status and ground-truth summary of events',                    roles: ['Analyst', 'Manager'],                   link: null                               },
    eei:             { description: 'Essential Elements of Information — specific data points required to satisfy the ICR',                roles: ['Analyst'],                              link: null                               },
    collection_task: { description: 'Collection Task — directed collection activity assigned to a source or asset',                        roles: ['Collection Manager'],                   link: null                               },
    iir:             { description: 'Intelligence Information Report — raw intelligence sourced from collection activities',               roles: ['Collector'],                            link: null                               },
    draft:           { description: null,                                                                                                  roles: ['Analyst'],                              link: null                               },
    review:          { description: null,                                                                                                  roles: ['Reviewer'],                             link: null                               },
    fintel:          { description: null,                                                                                                  roles: ['Analyst', 'Briefer'],                   link: '/products?filter=fintel'          },
    disclosure:      { description: null,                                                                                                  roles: ['Disclosure Officer'],                   link: null                               },
};

// ── custom node component (all nodes use this) ────────────────────────────────
const HANDLE_STYLE = {
    width: 8, height: 8,
    background: '#94A3B8',
    border: '2px solid #fff',
    borderRadius: '50%',
};

function WorkflowNode({ data }) {
    const c      = paletteByKey[data.colorKey] ?? paletteByKey.slate;
    const roles  = data.roles ?? [];
    const hasExtra = data.description || roles.length > 0;

    return (
        <>
            <Handle type="target" position={Position.Left}   style={HANDLE_STYLE} />
            <Handle type="target" position={Position.Top}    style={HANDLE_STYLE} />
            <div style={{
                background: c.tint,
                borderLeft: `3px solid ${c.accent}`,
                borderTop: '1px solid rgba(26,29,33,0.10)',
                borderRight: '1px solid rgba(26,29,33,0.10)',
                borderBottom: '1px solid rgba(26,29,33,0.10)',
                color: c.text,
                borderRadius: 8,
                padding: '9px 14px',
                minWidth: roles.length > 0 ? 180 : 130,
                maxWidth: 240,
                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
                cursor: 'grab',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}>
                {/* label row: title + optional open link */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: hasExtra ? 5 : 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>
                        {data.label}
                    </div>
                    {data.link && (
                        <a
                            href={data.link}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            title="Open"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 2,
                                fontSize: 9, fontWeight: 600, letterSpacing: 0.3,
                                color: c.accent,
                                background: `${c.accent}14`,
                                border: `1px solid ${c.accent}33`,
                                borderRadius: 3,
                                padding: '1px 5px',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                cursor: 'pointer',
                            }}
                        >
                            Open ↗
                        </a>
                    )}
                </div>

                {/* description */}
                {data.description && (
                    <div style={{
                        fontSize: 9, fontWeight: 400, lineHeight: 1.35, color: '#64748B',
                        borderTop: `1px solid ${c.accent}22`,
                        paddingTop: 4, marginBottom: roles.length > 0 ? 5 : 0,
                    }}>
                        {data.description}
                    </div>
                )}

                {/* role chips */}
                {roles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: data.description ? 0 : 4 }}>
                        {roles.map((rk) => {
                            const r = roleByKey[rk];
                            if (!r) return null;
                            return (
                                <span key={rk} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    fontSize: 9, fontWeight: 600, letterSpacing: 0.3,
                                    color: r.color,
                                    background: r.bg,
                                    border: `1px solid ${r.color}33`,
                                    borderRadius: 4,
                                    padding: '1px 5px',
                                    lineHeight: '14px',
                                }}>
                                    <span style={{
                                        width: 5, height: 5, borderRadius: '50%',
                                        background: r.color, display: 'inline-block', flexShrink: 0,
                                    }} />
                                    {r.label}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
            <Handle type="source" position={Position.Right}  style={HANDLE_STYLE} />
            <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
        </>
    );
}

const NODE_TYPES = { workflow: WorkflowNode };

// ── default diagram ───────────────────────────────────────────────────────────
function mkNode(id, x, y, label, colorKey, role = null) {
    const meta = STEP_META[id] ?? {};
    const link = (role && meta.roleLinks?.[role]) ?? meta.link ?? null;
    return {
        id,
        type: 'workflow',
        position: { x, y },
        data: { label, colorKey, description: meta.description ?? null, roles: meta.roles ?? [], link },
    };
}

function makeDefaultNodes(role = null) {
    return [
        mkNode('pitch',           -140, 210, 'PITCH',             'indigo', role),
        mkNode('icr',             195,  200, 'ICR',               'violet', role),
        mkNode('sitrep',          370,  20,  'SITREP',            'stone',  role),
        mkNode('eei',             560,  315, 'EEI',               'blue',   role),
        mkNode('collection_task', 560,  455, 'Collection Task',   'slate',  role),
        mkNode('iir',             840,  455, 'IIR',               'mauve',  role),
        mkNode('draft',           560,  120, 'Draft Report',      'amber',  role),
        mkNode('review',          790,  120, 'Review & Approval', 'rose',   role),
        mkNode('fintel',          1060, 120, 'FINTEL',            'green',  role),
        mkNode('disclosure',      1300, 120, 'DISCLOSURE',        'teal',   role),
    ];
}

const DEFAULT_EDGES = [
    mkEdge('pitch-icr',           'pitch',           'icr',             { label: 'creates'    }),
    mkEdge('icr-draft',           'icr',             'draft',           { label: 'per ICR'    }),
    mkEdge('draft-review',        'draft',           'review'                                  ),
    mkEdge('review-fintel',       'review',          'fintel',          { label: 'approved'   }),
    mkEdge('fintel-disc',         'fintel',          'disclosure',      { label: 'write'      }),
    mkEdge('icr-sitrep',          'icr',             'sitrep',          { label: 'as needed', dashed: true }),
    mkEdge('icr-eei',             'icr',             'eei'                                     ),
    mkEdge('eei-collection_task', 'eei',             'collection_task'                         ),
    mkEdge('collection_task-iir', 'collection_task', 'iir'                                     ),
];

function cloneDefaults(role = null) {
    return {
        nodes: makeDefaultNodes(role).map((n) => ({ ...n, data: { ...n.data } })),
        edges: DEFAULT_EDGES.map((e) => ({ ...e, style: { ...e.style } })),
    };
}

let nodeCounter = 100;

// ── colour picker item ────────────────────────────────────────────────────────
function ColorItem({ p }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
                width: 14, height: 14, borderRadius: '3px', flexShrink: 0,
                bgcolor: p.tint,
                border: `1px solid rgba(0,0,0,0.12)`, borderLeftWidth: 3, borderLeftColor: p.accent,
            }} />
            <Typography variant="body2">{p.label}</Typography>
        </Box>
    );
}

// ── shared step dialog ────────────────────────────────────────────────────────
function StepDialog({ open, onClose, onSave, title, label, setLabel, color, setColor }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: 15 }}>{title}</DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Step name"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && label.trim() && onSave()}
                    autoFocus fullWidth size="small"
                />
                <FormControl fullWidth size="small">
                    <InputLabel>Colour</InputLabel>
                    <Select value={color} label="Colour" onChange={(e) => setColor(e.target.value)}>
                        {PALETTE.map((p) => (
                            <MenuItem key={p.key} value={p.key}><ColorItem p={p} /></MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
                <Button onClick={onSave} disabled={!label.trim()} variant="contained" size="small"
                    disableElevation sx={{ textTransform: 'none', borderRadius: 1 }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── role filter strip (rendered outside ReactFlow canvas) ────────────────────
function RoleFilterStrip({ selectedRole, onSelect }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: 'text.disabled', textTransform: 'uppercase', mr: 0.5 }}>
                Filter by role:
            </Typography>
            {ROLES.map((r) => {
                const active = selectedRole === r.key;
                return (
                    <Box
                        key={r.key}
                        component="button"
                        onClick={() => onSelect(active ? null : r.key)}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                            px: 1.2, py: 0.4,
                            fontSize: 11, fontWeight: active ? 700 : 500,
                            color: active ? r.color : 'text.secondary',
                            background: active ? r.bg : 'transparent',
                            border: `1px solid ${active ? r.color + '55' : 'rgba(0,0,0,0.12)'}`,
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { background: r.bg, borderColor: r.color + '55', color: r.color },
                            outline: 'none',
                            fontFamily: 'inherit',
                        }}
                    >
                        <Box component="span" sx={{
                            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                            bgcolor: active ? r.color : '#CBD5E1',
                            transition: 'background 0.15s',
                        }} />
                        {r.label}
                    </Box>
                );
            })}
            {selectedRole && (
                <Box
                    component="button"
                    onClick={() => onSelect(null)}
                    sx={{
                        fontSize: 10, color: 'text.disabled', cursor: 'pointer',
                        border: 'none', background: 'none', fontFamily: 'inherit',
                        '&:hover': { color: 'text.secondary' }, outline: 'none',
                    }}
                >
                    × clear
                </Box>
            )}
        </Stack>
    );
}

// ── main component ────────────────────────────────────────────────────────────
const WorkflowPage = () => {
    const { role } = useSession();
    const { nodes: dn, edges: de } = cloneDefaults(role);
    const [nodes, setNodes, onNodesChange] = useNodesState(dn);
    const [edges, setEdges, onEdgesChange] = useEdgesState(de);
    const [selected, setSelected]          = useState({ nodes: [], edges: [] });
    const [selectedRole, setSelectedRole]  = useState(null);

    const [addOpen, setAddOpen]   = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [newColor, setNewColor] = useState('slate');

    const [editOpen, setEditOpen]   = useState(false);
    const [editId, setEditId]       = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editColor, setEditColor] = useState('slate');

    const [snack, setSnack] = useState({ open: false, msg: '' });

    const wrapperRef    = useRef(null);
    const [rfi, setRfi] = useState(null);

    const toast = (msg) => setSnack({ open: true, msg });

    // Apply opacity when a role filter is active
    const displayedNodes = useMemo(() => {
        if (!selectedRole) return nodes;
        return nodes.map((n) => {
            const nodeRoles = n.data.roles ?? [];
            const match = nodeRoles.includes(selectedRole);
            return { ...n, style: { ...(n.style ?? {}), opacity: match ? 1 : 0.18, transition: 'opacity 0.2s' } };
        });
    }, [nodes, selectedRole]);

    const onConnect = useCallback(
        (params) => setEdges((es) => addEdge({ ...EDGE_BASE, ...params }, es)),
        [setEdges],
    );

    const onSelectionChange = useCallback(
        ({ nodes: sn, edges: se }) => setSelected({ nodes: sn, edges: se }),
        [],
    );

    const openEdit = useCallback((node) => {
        setEditId(node.id);
        setEditLabel(node.data.label);
        setEditColor(node.data.colorKey ?? 'slate');
        setEditOpen(true);
    }, []);

    const onNodeDoubleClick = useCallback((_, node) => openEdit(node), [openEdit]);

    const handleAdd = () => {
        if (!newLabel.trim()) return;
        const id = `node_${++nodeCounter}`;
        const center = rfi
            ? rfi.project({ x: (wrapperRef.current?.clientWidth ?? 600) / 2, y: (wrapperRef.current?.clientHeight ?? 400) / 2 })
            : { x: 500, y: 200 };
        setNodes((ns) => [
            ...ns,
            { id, type: 'workflow', position: center, data: { label: newLabel.trim(), colorKey: newColor, roles: [] } },
        ]);
        toast(`"${newLabel.trim()}" added`);
        setNewLabel('');
        setNewColor('slate');
        setAddOpen(false);
    };

    const handleSaveEdit = () => {
        setNodes((ns) =>
            ns.map((n) =>
                n.id === editId
                    ? { ...n, data: { ...n.data, label: editLabel.trim(), colorKey: editColor } }
                    : n,
            ),
        );
        setEditOpen(false);
    };

    const handleDelete = () => {
        const nids = new Set(selected.nodes.map((n) => n.id));
        const eids = new Set(selected.edges.map((e) => e.id));
        setNodes((ns) => ns.filter((n) => !nids.has(n.id)));
        setEdges((es) => es.filter((e) => !eids.has(e.id) && !nids.has(e.source) && !nids.has(e.target)));
        setSelected({ nodes: [], edges: [] });
        const count = nids.size + eids.size;
        toast(`Removed ${count} element${count !== 1 ? 's' : ''}`);
    };

    const handleReset = () => {
        const { nodes: rn, edges: re } = cloneDefaults(role);
        setNodes(rn);
        setEdges(re);
        setSelectedRole(null);
        toast('Diagram reset');
    };

    const hasSel = selected.nodes.length > 0 || selected.edges.length > 0;

    return (
        <Stack p={2} spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>

            {/* ── header ── */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Stack spacing={0.5}>
                    <Typography variant="h5" fontWeight={600}>Workflow Designer</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Intelligence production lifecycle · drag to reposition · double-click to edit · drag handles to connect
                    </Typography>
                </Stack>
            </Stack>

            {/* ── role filter strip ── */}
            <RoleFilterStrip selectedRole={selectedRole} onSelect={setSelectedRole} />

            {/* ── canvas ── */}
            <Paper
                variant="outlined"
                ref={wrapperRef}
                sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', minHeight: 480, position: 'relative' }}
            >
                <ReactFlow
                    nodes={displayedNodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onSelectionChange={onSelectionChange}
                    onNodeDoubleClick={onNodeDoubleClick}
                    onInit={setRfi}
                    nodeTypes={NODE_TYPES}
                    nodesDraggable
                    nodesConnectable
                    elementsSelectable
                    deleteKeyCode="Delete"
                    multiSelectionKeyCode="Shift"
                    fitView
                    fitViewOptions={{ padding: 0.25 }}
                    proOptions={{ hideAttribution: true }}
                    style={{ background: '#FAFBFC' }}
                >
                    <Background color="#E2E8F0" gap={20} size={1} />
                    <Controls style={{ bottom: 16, left: 16, boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)', borderRadius: 8 }} />
                    <MiniMap
                        nodeColor={(n) => paletteByKey[n.data?.colorKey]?.accent ?? '#94A3B8'}
                        maskColor="rgba(248,250,252,0.75)"
                        style={{ border: '1px solid rgba(26,29,33,0.08)', borderRadius: 8, background: '#fff' }}
                    />

                    {/* ── action toolbar (top-right) ── */}
                    <Panel position="top-right">
                        <Paper variant="outlined" sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            p: '5px 8px', borderRadius: 1.5, bgcolor: 'background.paper',
                            boxShadow: '0 1px 4px 0 rgba(0,0,0,0.08)',
                        }}>
                            <Tooltip title="Add step" placement="bottom">
                                <Button onClick={() => setAddOpen(true)} size="small"
                                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                    disableElevation sx={tbBtn}>
                                    Add step
                                </Button>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title={selected.nodes.length === 1 ? 'Edit step' : 'Select one node to edit'} placement="bottom">
                                <span>
                                    <Button
                                        onClick={() => selected.nodes.length === 1 && openEdit(selected.nodes[0])}
                                        disabled={selected.nodes.length !== 1}
                                        size="small"
                                        startIcon={<EditOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                                        disableElevation sx={tbBtn}>
                                        Edit
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={hasSel ? 'Delete selected' : 'Select elements to delete'} placement="bottom">
                                <span>
                                    <Button onClick={handleDelete} disabled={!hasSel} size="small"
                                        startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px !important' }} />}
                                        disableElevation
                                        sx={{ ...tbBtn, '&:not(:disabled)': { color: 'error.main' } }}>
                                        Delete
                                    </Button>
                                </span>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="Reset to default diagram" placement="bottom">
                                <Button onClick={handleReset} size="small"
                                    startIcon={<RestoreIcon sx={{ fontSize: '15px !important' }} />}
                                    disableElevation sx={{ ...tbBtn, color: 'text.secondary' }}>
                                    Reset
                                </Button>
                            </Tooltip>
                        </Paper>
                    </Panel>
                </ReactFlow>
            </Paper>

            <StepDialog open={addOpen} onClose={() => setAddOpen(false)} onSave={handleAdd}
                title="Add workflow step" label={newLabel} setLabel={setNewLabel}
                color={newColor} setColor={setNewColor} />

            <StepDialog open={editOpen} onClose={() => setEditOpen(false)} onSave={handleSaveEdit}
                title="Edit workflow step" label={editLabel} setLabel={setEditLabel}
                color={editColor} setColor={setEditColor} />

            <Snackbar open={snack.open} autoHideDuration={2200}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="success" variant="filled"
                    sx={{ borderRadius: 1.5, fontSize: 13, boxShadow: 2 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

const tbBtn = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 12,
    borderRadius: 1,
    px: 1,
    py: 0.5,
    minWidth: 0,
    color: 'text.primary',
    '&:hover': { bgcolor: 'action.hover' },
};

export default WorkflowPage;
